import { useState } from 'react'
import { api, ApiError, postForm, requestBlob } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { date, isoDate, shortDate } from '@/lib/format'
import { useAsync } from '@/lib/useAsync'
import { AuditHistoryButton } from '@/components/AuditLog'
import { PageHeader } from '@/components/ui/page'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Checkbox, Field, Input, Textarea } from '@/components/ui/form'
import { Table, Td, Th } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type { Paged, TermsDocumentSummary, TermsVersionStatusName } from '@/types/api'

const TONES: Record<TermsVersionStatusName, 'positive' | 'neutral' | 'warning'> = {
  Published: 'positive',
  Draft: 'warning',
  Superseded: 'neutral',
  Archived: 'neutral',
}

/** What a country with no selection resolves to, so the footer still reads "No rows". */
const EMPTY: Paged<TermsDocumentSummary> = {
  items: [],
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  totalCount: 0,
  totalPages: 0,
  hasMore: false,
}

export function Terms() {
  const { can } = useAuth()
  const { current } = useCountry()
  const toast = useToast()
  const [creating, setCreating] = useState(false)
  const [uploadingTo, setUploadingTo] = useState<TermsDocumentSummary | undefined>()
  const [removing, setRemoving] = useState<TermsDocumentSummary | undefined>()
  const [search, setSearch] = useState('')
  const [onlyDefault, setOnlyDefault] = useState('')
  const [sort, setSort] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  const list = useAsync<Paged<TermsDocumentSummary>>(
    (signal) =>
      current
        ? api.get<Paged<TermsDocumentSummary>>(
            `/countries/${current.countryId}/terms`,
            { search, isDefault: onlyDefault === '' ? undefined : onlyDefault === 'yes', sort, page, pageSize },
            signal,
          )
        : Promise.resolve(EMPTY),
    [current?.countryId, search, onlyDefault, sort, page, pageSize],
  )

  async function act(run: () => Promise<unknown>, success: string) {
    try {
      await run()
      toast.show(success, 'success')
      list.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'That did not work', 'error')
    }
  }

  async function download(versionId: string, fileName: string) {
    try {
      const blob = await requestBlob(`/terms/versions/${versionId}/download`)
      const href = URL.createObjectURL(blob)
      const anchor = document.createElement('a')

      anchor.href = href
      anchor.download = fileName
      anchor.click()
      URL.revokeObjectURL(href)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not download the file', 'error')
    }
  }

  const noneInForce = list.data?.items.every((document) => !document.current) ?? false

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Terms and Conditions"
        hint="Counsel's document, uploaded and versioned. QuoteHero stores and attaches it - it never authors or re-typesets legal text."
        actions={
          can('terms.version.upload') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New document
            </Button>
          )
        }
      />

      {list.data && list.data.items.length > 0 && noneInForce && (
        <Card className="mb-5 border-warn/30 bg-warn-wash">
          <div className="px-5 py-4 text-[13px] text-warn">
            <p className="font-medium">{current?.name} cannot issue quotations yet.</p>
            <p className="mt-1">
              Nothing is published and in force. A quotation freezes the terms it was issued
              under, so there is nothing for it to freeze.
            </p>
          </div>
        </Card>
      )}

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: '', label: 'Default first' },
          { value: 'name', label: 'Name A–Z' },
          { value: '-name', label: 'Name Z–A' },
          { value: 'status', label: 'Status' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search document name"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect
          label="Default"
          value={onlyDefault}
          onChange={(next) => refine(() => setOnlyDefault(next))}
        >
          <option value="">Every document</option>
          <option value="yes">Country default only</option>
          <option value="no">Everything else</option>
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data?.items.length === 0 && (
          <Card>
            <EmptyState
              title="No terms document yet"
              hint={`${current?.name ?? 'This country'} cannot issue a single quotation until counsel's document is uploaded and published.`}
              action={
                can('terms.version.upload') && (
                  <Button variant="primary" onClick={() => setCreating(true)}>
                    Add the document
                  </Button>
                )
              }
            />
          </Card>
        )}

        {list.data?.items.map((document) => (
          <Card key={document.id} className="mb-5">
            <CardHeader
              title={document.name}
              hint={
                document.current
                  ? `Version ${document.current.versionNo} is in force, effective ${date(document.current.effectiveFrom)}.`
                  : 'Nothing published and in force.'
              }
              action={
                <div className="flex items-center gap-2">
                  {document.isDefault ? (
                    <Badge tone="accent">Country default</Badge>
                  ) : (
                    can('terms.default.set') && (
                      <Button
                        size="sm"
                        onClick={() =>
                          void act(() => api.put(`/terms/${document.id}/default`), 'Set as the country default')
                        }
                      >
                        Make default
                      </Button>
                    )
                  )}
                  {can('terms.version.upload') && (
                    <Button size="sm" onClick={() => setUploadingTo(document)}>
                      Upload version
                    </Button>
                  )}
                  {/* Only for a document nothing was ever uploaded to - a duplicate created
                      by mistake, which otherwise sits here for ever saying nothing is
                      published. Once a version exists the document carries legal history
                      and the button is gone rather than disabled: there is nothing the
                      person could do to make it apply. */}
                  {document.versions.length === 0 && can('terms.document.delete') && (
                    <Button size="sm" onClick={() => setRemoving(document)}>
                      Remove
                    </Button>
                  )}
                  <AuditHistoryButton
                    entityType="TermsDocument"
                    entityId={document.id}
                    title={document.name}
                  />
                </div>
              }
            />

            {document.versions.length === 0 ? (
              <EmptyState title="No versions uploaded" hint="Upload counsel's PDF to get started." />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Version</Th>
                    <Th>Effective</Th>
                    <Th>What changed</Th>
                    <Th>Checksum</Th>
                    <Th>Status</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {document.versions.map((version) => (
                    <tr key={version.id}>
                      <Td>
                        <span className="tabular font-medium">v{version.versionNo}</span>
                        <span className="block text-[12px] text-muted">{version.fileName}</span>
                        <span className="block text-[12px] text-faint">
                          by {version.uploadedBy}, {shortDate(version.uploadedAt)}
                        </span>
                      </Td>
                      <Td className="text-muted">{shortDate(version.effectiveFrom)}</Td>
                      <Td className="max-w-xs text-[13px] text-muted">{version.changeNote ?? '-'}</Td>
                      <Td>
                        {/* The checksum is what proves, years later, which bytes a customer
                            was actually sent. */}
                        <span className="tabular text-[12px] text-faint" title={version.sha256}>
                          {version.sha256.slice(0, 12)}…
                        </span>
                      </Td>
                      <Td>
                        <Badge tone={TONES[version.status]}>{version.status}</Badge>
                        {version.publishedBy && (
                          <span className="block text-[12px] text-muted">by {version.publishedBy}</span>
                        )}
                      </Td>
                      <Td>
                        <span className="flex justify-end gap-2">
                          {can('terms.download') && (
                            <Button size="sm" onClick={() => void download(version.id, version.fileName)}>
                              Download
                            </Button>
                          )}
                          {version.status === 'Draft' && can('terms.publish') && (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() =>
                                void act(
                                  () => api.post(`/terms/versions/${version.id}/publish`),
                                  `Version ${version.versionNo} published`,
                                )
                              }
                            >
                              Publish
                            </Button>
                          )}
                          {version.status !== 'Archived' && can('terms.archive') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                void act(
                                  () => api.post(`/terms/versions/${version.id}/archive`),
                                  'Archived',
                                )
                              }
                            >
                              Archive
                            </Button>
                          )}
                        </span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        ))}
      </Busy>

      {/* A card stack rather than a table, so the footer stands on its own. It still
          renders for a single document: the count and the rows-per-page control are how
          somebody confirms they are seeing every document this country has. */}
      {list.data && (
        <Card>
          <Pagination
            page={list.data.page}
            pageSize={list.data.pageSize}
            totalCount={list.data.totalCount}
            totalPages={list.data.totalPages}
            onPageChange={setPage}
            onPageSizeChange={(next) => refine(() => setPageSize(next))}
          />
        </Card>
      )}

      <NewDocument
        open={creating}
        countryId={current?.countryId}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false)
          list.reload()
        }}
      />

      <Dialog
        open={removing !== undefined}
        title={removing ? `Remove ${removing.name}?` : 'Remove this document?'}
        onClose={() => setRemoving(undefined)}
        footer={
          <>
            <Button size="sm" onClick={() => setRemoving(undefined)}>
              Keep it
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                const target = removing
                setRemoving(undefined)
                if (target) void act(() => api.del(`/terms/${target.id}`), `${target.name} removed`)
              }}
            >
              Remove
            </Button>
          </>
        }
      >
        {/* Says what it costs rather than asking whether you are sure. Nothing was ever
            uploaded to this document, so the answer is genuinely "nothing". */}
        <p className="text-[13px] text-ink-2">
          Nothing has been uploaded to it, so no quotation refers to it and no version is
          lost. Create it again if it turns out to be needed.
        </p>
      </Dialog>

      <UploadVersion
        document={uploadingTo}
        onClose={() => setUploadingTo(undefined)}
        onUploaded={() => {
          setUploadingTo(undefined)
          list.reload()
        }}
      />
    </>
  )
}

function NewDocument({
  open,
  countryId,
  onClose,
  onCreated,
}: {
  open: boolean
  countryId: string | undefined
  onClose: () => void
  onCreated: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState('Standard terms of supply')
  const [isDefault, setIsDefault] = useState(true)
  const [saving, setSaving] = useState(false)

  async function create() {
    if (!countryId) return

    setSaving(true)
    try {
      await api.post(`/countries/${countryId}/terms`, { name, isDefault })
      onCreated()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not create it', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New terms document"
      description="A named document with its own version history. Most countries need exactly one."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!name} onClick={() => void create()}>
            Create
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          {(id) => <Input id={id} value={name} onChange={(event) => setName(event.target.value)} />}
        </Field>
        <Checkbox
          label="This is the country's default document"
          checked={isDefault}
          onChange={(event) => setIsDefault(event.target.checked)}
        />
      </div>
    </Dialog>
  )
}

function UploadVersion({
  document: target,
  onClose,
  onUploaded,
}: {
  document: TermsDocumentSummary | undefined
  onClose: () => void
  onUploaded: () => void
}) {
  const toast = useToast()
  const [file, setFile] = useState<File | undefined>()
  const [effectiveFrom, setEffectiveFrom] = useState(isoDate())
  const [changeNote, setChangeNote] = useState('')
  const [saving, setSaving] = useState(false)

  async function upload() {
    if (!target || !file) return

    setSaving(true)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('effectiveFrom', effectiveFrom)
      if (changeNote) form.append('changeNote', changeNote)

      await postForm(`/terms/${target.id}/versions`, form)

      toast.show('Uploaded as a draft. Publish it when counsel confirms.', 'success')
      onUploaded()
      setFile(undefined)
      setChangeNote('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not upload', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={target !== undefined}
      title="Upload a version"
      description="Uploaded versions arrive as drafts. Publishing is a separate step, because a file landing here is not the same act as deciding customers are bound by it."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!file} onClick={() => void upload()}>
            Upload
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Document" required hint="PDF only, up to 20 MB. Counsel's file exactly as supplied.">
          {(id) => (
            <input
              id={id}
              type="file"
              accept="application/pdf"
              onChange={(event) => setFile(event.target.files?.[0])}
              className="w-full rounded-md border border-line bg-surface px-3 py-2 text-sm"
            />
          )}
        </Field>

        <Field
          label="Effective from"
          required
          hint="Can be in the future: the current version stays in force until this date arrives."
        >
          {(id) => (
            <Input
              id={id}
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          )}
        </Field>

        <Field label="What changed" hint="Read by whoever publishes it, and kept with the version forever.">
          {(id) => (
            <Textarea id={id} value={changeNote} onChange={(event) => setChangeNote(event.target.value)} />
          )}
        </Field>
      </div>
    </Dialog>
  )
}
